import type { FormDocument, FormFieldDef, FormSectionDef } from '@los-workspace/workflow-runtime';
import {
  isAddressPackSection,
  PACK_ADDRESS_FIELD_MAP,
  packSectionTitle,
} from './pack-section.registry';
import { coerceText } from '../../utils/coerce-text';

export type PackAddressSemanticKey =
  | 'addressLine1'
  | 'addressLine2'
  | 'pinCode'
  | 'city'
  | 'district'
  | 'state'
  | 'country'
  | 'addressType';

export type PackAddressFieldMap = Record<PackAddressSemanticKey, string>;

export type PackApiBindings = {
  fullName?: string;
  mobile?: string;
  email?: string;
  date?: string;
  gender?: string;
  product?: string;
  scheme?: string;
  loanAmount?: string;
  tenureMonths?: string;
  grossMonthlyIncome?: string;
  existingMonthlyEmi?: string;
  sourceType?: string;
  sourceDescription?: string;
  preferredBranch?: string;
  priority?: string;
  priorityRemark?: string;
  address: PackAddressFieldMap;
};

const ADDRESS_LABEL_RULES: Array<[PackAddressSemanticKey, RegExp]> = [
  ['addressLine1', /address\s*line\s*1/i],
  ['addressLine2', /address\s*line\s*2/i],
  ['pinCode', /pin\s*code|pincode|postal/i],
  ['city', /^city$/i],
  ['district', /district/i],
  ['state', /^state$/i],
  ['country', /country/i],
  ['addressType', /address\s*type/i],
];

const DEFAULT_ADDRESS_MAP: PackAddressFieldMap = {
  ...PACK_ADDRESS_FIELD_MAP,
} as PackAddressFieldMap;

function normLabel(value: unknown): string {
  return coerceText(value ?? '').trim();
}

function findAddressSection(formDoc: FormDocument | null | undefined): FormSectionDef | undefined {
  if (!formDoc?.sections?.length) return undefined;
  return formDoc.sections.find((section, index) =>
    isAddressPackSection(section, index),
  );
}

/** Resolve semantic address keys → pack fieldCodes from workflow JSON. */
export function resolveAddressFieldMap(
  formDoc: FormDocument | null | undefined,
): PackAddressFieldMap {
  const section = findAddressSection(formDoc);
  if (!section?.fields?.length) {
    return { ...DEFAULT_ADDRESS_MAP };
  }

  const resolved = { ...DEFAULT_ADDRESS_MAP };
  const usedCodes = new Set<string>();

  for (const [semanticKey, pattern] of ADDRESS_LABEL_RULES) {
    const field = (section.fields ?? []).find(
      (item) =>
        pattern.test(normLabel(item.label)) && !usedCodes.has(item.fieldCode),
    );
    if (field?.fieldCode) {
      resolved[semanticKey] = field.fieldCode;
      usedCodes.add(field.fieldCode);
    }
  }

  return resolved;
}

function fieldByLabel(
  fields: FormFieldDef[],
  pattern: RegExp,
  used: Set<string>,
): FormFieldDef | undefined {
  return fields.find(
    (field) => pattern.test(normLabel(field.label)) && !used.has(field.fieldCode),
  );
}

function fieldByType(
  fields: FormFieldDef[],
  fieldType: string,
  used: Set<string>,
  ordinal = 0,
): FormFieldDef | undefined {
  const matches = fields.filter(
    (field) =>
      String(field.fieldType ?? '').toUpperCase() === fieldType.toUpperCase() &&
      !used.has(field.fieldCode),
  );
  return matches[ordinal];
}

function sectionByTitle(
  formDoc: FormDocument,
  pattern: RegExp,
): FormSectionDef | undefined {
  return formDoc.sections?.find((section, index) =>
    pattern.test(packSectionTitle(section, index)),
  );
}

export function findPackFormField(
  formDoc: FormDocument | null | undefined,
  fieldCode: string,
): FormFieldDef | undefined {
  const code = fieldCode.trim();
  if (!code) return undefined;

  for (const section of formDoc?.sections ?? []) {
    const match = section.fields?.find((field) => field.fieldCode === code);
    if (match) return match;
  }

  return undefined;
}

/** Resolve pack fieldCodes for CreateOpportunityRequest mapping from workflow JSON. */
export function resolvePackApiBindings(
  formDoc: FormDocument | null | undefined,
): PackApiBindings {
  const address = resolveAddressFieldMap(formDoc);
  if (!formDoc?.sections?.length) {
    return defaultPackApiBindings(address);
  }

  const used = new Set<string>();
  const bindings: PackApiBindings = { address };

  bindBasicSectionFields(formDoc, bindings, used);
  bindLoanSectionFields(formDoc, bindings);
  bindSourceSectionFields(formDoc, bindings);
  bindPrioritySectionFields(formDoc, bindings);

  return bindings;
}

function defaultPackApiBindings(address: PackAddressFieldMap): PackApiBindings {
  return {
    address,
    fullName: 'textField',
    mobile: 'phone',
    email: 'email',
    date: 'date',
    gender: 'radio',
    product: 'dropdown',
    scheme: 'dropdown2',
    loanAmount: 'currency',
    tenureMonths: 'number',
    grossMonthlyIncome: 'currency2',
    existingMonthlyEmi: 'currency3',
    sourceType: 'dropdown3',
    sourceDescription: 'dropdown4',
    preferredBranch: 'dropdown5',
    priority: 'radio3',
    priorityRemark: 'longText',
  };
}

function bindBasicSectionFields(
  formDoc: FormDocument,
  bindings: PackApiBindings,
  used: Set<string>,
): void {
  const basic = sectionByTitle(formDoc, /basic/i);
  if (!basic?.fields?.length) return;

  const nameField =
    fieldByLabel(basic.fields, /(applicant|organization)\s*name/i, used) ??
    fieldByType(basic.fields, 'TEXT', used, 0);
  if (nameField) {
    bindings.fullName = nameField.fieldCode;
    used.add(nameField.fieldCode);
  }

  const phoneField = fieldByType(basic.fields, 'PHONE', used, 0);
  if (phoneField) {
    bindings.mobile = phoneField.fieldCode;
    used.add(phoneField.fieldCode);
  }

  const emailField = fieldByType(basic.fields, 'EMAIL', used, 0);
  if (emailField) {
    bindings.email = emailField.fieldCode;
    used.add(emailField.fieldCode);
  }

  const dateField = fieldByType(basic.fields, 'DATE', used, 0);
  if (dateField) {
    bindings.date = dateField.fieldCode;
    used.add(dateField.fieldCode);
  }

  const genderField =
    fieldByLabel(basic.fields, /gender/i, used) ??
    fieldByType(basic.fields, 'RADIO', used, 0);
  if (genderField) {
    bindings.gender = genderField.fieldCode;
    used.add(genderField.fieldCode);
  }
}

function bindLoanSectionFields(formDoc: FormDocument, bindings: PackApiBindings): void {
  const loan = sectionByTitle(formDoc, /loan/i);
  if (!loan?.fields?.length) return;

  const loanUsed = new Set<string>();
  const productField =
    fieldByLabel(loan.fields, /^product$/i, loanUsed) ??
    fieldByType(loan.fields, 'DROPDOWN', loanUsed, 0);
  if (productField) bindings.product = productField.fieldCode;

  const schemeField =
    fieldByLabel(loan.fields, /scheme/i, loanUsed) ??
    fieldByType(loan.fields, 'DROPDOWN', loanUsed, 1);
  if (schemeField) bindings.scheme = schemeField.fieldCode;

  const loanAmountField =
    fieldByLabel(loan.fields, /loan\s*amount/i, loanUsed) ??
    fieldByType(loan.fields, 'CURRENCY', loanUsed, 0);
  if (loanAmountField) bindings.loanAmount = loanAmountField.fieldCode;

  const tenureField =
    fieldByLabel(loan.fields, /tenure/i, loanUsed) ??
    fieldByType(loan.fields, 'NUMBER', loanUsed, 0);
  if (tenureField) bindings.tenureMonths = tenureField.fieldCode;

  const incomeField =
    fieldByLabel(loan.fields, /gross\s*monthly\s*income/i, loanUsed) ??
    fieldByType(loan.fields, 'CURRENCY', loanUsed, 1);
  if (incomeField) bindings.grossMonthlyIncome = incomeField.fieldCode;

  const emiField =
    fieldByLabel(loan.fields, /existing\s*monthly\s*emi/i, loanUsed) ??
    fieldByType(loan.fields, 'CURRENCY', loanUsed, 2);
  if (emiField) bindings.existingMonthlyEmi = emiField.fieldCode;
}

function bindSourceSectionFields(formDoc: FormDocument, bindings: PackApiBindings): void {
  const source = sectionByTitle(formDoc, /source/i);
  if (!source?.fields?.length) return;

  const sourceUsed = new Set<string>();
  const sourceTypeField =
    fieldByLabel(source.fields, /source\s*type/i, sourceUsed) ??
    fieldByType(source.fields, 'DROPDOWN', sourceUsed, 0);
  if (sourceTypeField) bindings.sourceType = sourceTypeField.fieldCode;

  const sourceDescField =
    fieldByLabel(source.fields, /source\s*description/i, sourceUsed) ??
    fieldByType(source.fields, 'DROPDOWN', sourceUsed, 1);
  if (sourceDescField) bindings.sourceDescription = sourceDescField.fieldCode;

  const branchField =
    fieldByLabel(source.fields, /preferred\s*branch|branch/i, sourceUsed) ??
    fieldByType(source.fields, 'DROPDOWN', sourceUsed, 2);
  if (branchField) bindings.preferredBranch = branchField.fieldCode;
}

function bindPrioritySectionFields(formDoc: FormDocument, bindings: PackApiBindings): void {
  const prioritySection =
    sectionByTitle(formDoc, /priority|lead/i) ??
    formDoc.sections?.[formDoc.sections.length - 1];
  if (!prioritySection?.fields?.length) return;

  const priorityUsed = new Set<string>();
  const priorityField =
    fieldByLabel(prioritySection.fields, /priority/i, priorityUsed) ??
    fieldByType(prioritySection.fields, 'RADIO', priorityUsed, 0);
  if (priorityField) bindings.priority = priorityField.fieldCode;

  const remarkField =
    fieldByLabel(prioritySection.fields, /remark/i, priorityUsed) ??
    fieldByType(prioritySection.fields, 'TEXTAREA', priorityUsed, 0);
  if (remarkField) bindings.priorityRemark = remarkField.fieldCode;
}

/** Build address component label map from the address section in workflow JSON. */
export function buildAddressFieldLabels(
  formDoc: FormDocument | null | undefined,
): Record<string, { label?: string; placeholder?: string }> {
  const section = findAddressSection(formDoc);
  const map = resolveAddressFieldMap(formDoc);
  const inverse = Object.fromEntries(
    Object.entries(map).map(([semanticKey, fieldCode]) => [fieldCode, semanticKey]),
  ) as Record<string, PackAddressSemanticKey>;

  const labels: Record<string, { label?: string; placeholder?: string }> = {};
  for (const field of section?.fields ?? []) {
    const semanticKey = inverse[field.fieldCode];
    if (!semanticKey) continue;
    labels[semanticKey] = {
      label: field.label,
      placeholder: field.placeholder,
    };
  }
  return labels;
}
